import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Bell, LogOut } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Logo } from "@/components/site/Navbar";

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, []);
  return (
    <Popover onOpenChange={(o) => o && data.unread && api.post("/notifications/read-all").then(load)}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" className="relative p-2 rounded-full hover:bg-slate-100">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && <span data-testid="notifications-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-red-500 text-white grid place-items-center">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 max-h-96 overflow-auto">
        {data.items.length === 0 && <p className="p-4 text-sm text-slate-500">No notifications yet</p>}
        {data.items.map((n) => (
          <button key={n.id} onClick={() => n.link && nav(n.link)} className="block w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50">
            <p className="text-sm font-semibold text-slate-900">{n.title}</p>
            <p className="text-xs text-slate-600">{n.body}</p>
            <p className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at)}</p>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function PortalShell({ title, items, children, badge }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const link = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-red-50 text-red-600" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 lg:px-6 h-14 flex items-center gap-3">
          <Logo />
          <span className="hidden sm:inline text-xs font-bold uppercase tracking-wider text-slate-400 border-l border-slate-200 pl-3">{title}</span>
          {badge}
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden md:inline text-sm text-slate-600">{user?.name}</span>
            <Notifications />
            <button data-testid="logout-button" onClick={() => { logout(); nav("/"); }} className="p-2 rounded-full hover:bg-slate-100" aria-label="Log out"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        <nav className="lg:hidden flex gap-1 overflow-x-auto px-3 pb-2">
          {items.map((i) => <NavLink key={i.to} to={i.to} end={i.end} className={link} data-testid={`nav-m-${i.id}`}><i.icon className="w-4 h-4" />{i.label}</NavLink>)}
        </nav>
      </header>
      <div className="flex">
        <aside className="hidden lg:block w-60 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-56px)] p-3 space-y-1 sticky top-14 self-start">
          {items.map((i) => <NavLink key={i.to} to={i.to} end={i.end} className={link} data-testid={`nav-${i.id}`}><i.icon className="w-4 h-4" />{i.label}</NavLink>)}
        </aside>
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
